/**
 * wms moduli uchun umumiy turlar/konstantalar (API + veb + mobil).
 * Faqat wms moduli egasi tahrirlaydi.
 *
 * Asosiy statuslar (supply, batch, parcel, movement, inventory) — `statuses.ts` da (StatusPill).
 * Bu yerda — faqat ombor ichki jarayonlarining qo'shimcha holatlari va ma'lumotnomalari.
 */
import type { L10n } from '../i18n';
import type { Tone } from '../statuses';

export interface WmsDef extends L10n {
  tone: Tone;
}

type DefMap<K extends string> = Record<K, WmsDef>;
const def = <K extends string>(m: DefMap<K>) => m;

/** Zona turlari. */
export const ZONE_KINDS = def({
  receiving: { uz: 'Qabul', ru: 'Приёмка', en: 'Receiving', zh: '收货', tone: 'info' },
  storage: { uz: 'Saqlash', ru: 'Хранение', en: 'Storage', zh: '仓储', tone: 'accent' },
  packing: { uz: 'Qadoqlash', ru: 'Упаковка', en: 'Packing', zh: '打包', tone: 'violet' },
  shipping: { uz: 'Jo‘natish', ru: 'Отгрузка', en: 'Shipping', zh: '发货', tone: 'success' },
  returns: { uz: 'Qaytarishlar', ru: 'Возвраты', en: 'Returns', zh: '退货', tone: 'warning' },
});
export type ZoneKind = keyof typeof ZONE_KINDS;

/** Yacheyka turlari. */
export const CELL_KINDS = def({
  shelf: { uz: 'Javon', ru: 'Полка', en: 'Shelf', zh: '货架', tone: 'neutral' },
  pallet: { uz: 'Paddon', ru: 'Паллет', en: 'Pallet', zh: '托盘', tone: 'info' },
  floor: { uz: 'Pol', ru: 'Пол', en: 'Floor', zh: '地面', tone: 'neutral' },
  buffer: { uz: 'Bufer', ru: 'Буфер', en: 'Buffer', zh: '缓存区', tone: 'warning' },
});
export type CellKind = keyof typeof CELL_KINDS;

/** Buyurtma zaxirasi (band qilish). */
export const RESERVATION_STATUSES = def({
  active: { uz: 'Band', ru: 'В резерве', en: 'Busy', zh: '忙碌', tone: 'accent' },
  picked: { uz: 'Yig‘ildi', ru: 'Собран', en: 'Picked', zh: '已拣货', tone: 'success' },
  released: { uz: 'Bo‘shatildi', ru: 'Снят', en: 'Released', zh: '已释放', tone: 'neutral' },
});
export type ReservationStatus = keyof typeof RESERVATION_STATUSES;

/** Ombordagi buyurtma navbati (oms.order.confirmed dan keyingi fulfillment holati). */
export const ORDER_QUEUE_STATUSES = def({
  queued: { uz: 'Navbatda', ru: 'В очереди', en: 'Queued', zh: '排队中', tone: 'info' },
  batched: { uz: 'Partiyada', ru: 'В партии', en: 'In a batch', zh: '已入批次', tone: 'violet' },
  packed: { uz: 'Qadoqlandi', ru: 'Упакован', en: 'Packed', zh: '已打包', tone: 'success' },
  dispatched: { uz: 'Topshirildi', ru: 'Отгружен', en: 'Dispatched', zh: '已出库', tone: 'info' },
  handed_over: { uz: 'Kuryerga berildi', ru: 'Передан', en: 'Handed to the courier', zh: '已交配送员', tone: 'violet' },
  returned: { uz: 'Qaytdi', ru: 'Возвращён', en: 'Returned', zh: '已退回', tone: 'danger' },
  canceled: { uz: 'Bekor qilindi', ru: 'Отменён', en: 'Cancelled', zh: '已取消', tone: 'danger' },
});
export type OrderQueueStatus = keyof typeof ORDER_QUEUE_STATUSES;

/** Qadoqlash sessiyasi (buyurtma qulfi, 15 daqiqa). */
export const PACK_SESSION_STATUSES = def({
  active: { uz: 'Faol', ru: 'Активна', en: 'Active', zh: '启用', tone: 'accent' },
  done: { uz: 'Yakunlandi', ru: 'Завершена', en: 'Finished', zh: '已完成', tone: 'success' },
  canceled: { uz: 'Bekor qilindi', ru: 'Отменена', en: 'Cancelled', zh: '已取消', tone: 'neutral' },
});
export type PackSessionStatus = keyof typeof PACK_SESSION_STATUSES;

export const PACK_SESSION_TTL_MIN = 15;

/**
 * ISH JOYI QR'I. Ish joyi — qadoqlash stolidagi KOMPYUTER: u hech narsani skanerlamaydi,
 * ekranida qisqa kodi (`ST-4821`) va shu kodning QR'i turadi. Skanerlash har doim xodimning
 * TELEFONIDAN bo'ladi — shundan keyin ish joyi va uning ochiq partiyalari o'shanga o'tadi.
 *
 * Prefiks shuning uchun kerak: ombor skaneri bir xil maydonga ham tovar shtrix-kodini, ham
 * ish joyi kodini yuboradi — ekran ularni ajrata olishi shart.
 */
export const STATION_QR_PREFIX = 'KTS-STATION:';

/** QR ichiga yoziladigan matn. */
export function stationQr(code: string): string {
  return STATION_QR_PREFIX + code;
}

/**
 * Skanerlangan matndan ish joyi kodini ajratadi, ish joyi kodi bo'lmasa — `null`.
 *
 * Prefikssiz `ST-4821` ham qabul qilinadi: kamera ishlamay qolganda xodim kodni ekrandan
 * o'qib qo'lda teradi. Bu shakl tovar shtrix-kodi bilan chalkashmaydi (shtrix-kod — raqam).
 */
export function parseStationQr(raw: string): string | null {
  const s = raw.trim();
  const body = s.toUpperCase().startsWith(STATION_QR_PREFIX) ? s.slice(STATION_QR_PREFIX.length).trim() : s;
  return /^ST-\d{3,8}$/i.test(body) ? body.toUpperCase() : null;
}

/**
 * Qadoqlash skaneri natijalari — UI rang/ovoz shu bo'yicha (Stocker: cyan — davom, purple — yangi buyurtma).
 */
export const PACK_SCAN_RESULTS = def({
  opened: { uz: 'Yangi buyurtma ochildi', ru: 'Открыт новый заказ', en: 'A new order was opened', zh: '已开启新订单', tone: 'violet' },
  ok: { uz: 'Qabul qilindi', ru: 'Принято', en: 'Accepted', zh: '已接收', tone: 'info' },
  complete: { uz: 'Buyurtma to‘liq yig‘ildi', ru: 'Заказ собран', en: 'The order is fully picked', zh: '订单已拣齐', tone: 'success' },
  wrong_item: { uz: 'Bu tovar buyurtmada yo‘q', ru: 'Товара нет в заказе', en: 'This product is not in the order', zh: '该商品不在订单内', tone: 'danger' },
  already_complete: { uz: 'Bu tovar to‘liq skanerlangan', ru: 'Товар уже отсканирован', en: 'This product is already fully scanned', zh: '该商品已扫描完毕', tone: 'warning' },
  unknown_barcode: { uz: 'Shtrix-kod topilmadi', ru: 'Штрихкод не найден', en: 'Barcode not found', zh: '未找到条码', tone: 'danger' },
  no_order: { uz: 'Bu tovar uchun ochiq buyurtma yo‘q', ru: 'Нет заказа для товара', en: 'No open order for this product', zh: '该商品没有未完成订单', tone: 'warning' },
  order_canceled: { uz: 'Buyurtma bekor qilingan', ru: 'Заказ отменён', en: 'The order has been cancelled', zh: '订单已取消', tone: 'danger' },
  /** Bekor arizasi / markirovka to'sig'i — buyurtma yig'ishga umuman chiqmaydi (`order_blocks`). */
  blocked: { uz: 'To‘xtatilgan — yig‘ilmaydi', ru: 'Остановлен — не собирать', en: 'Stopped — do not assemble', zh: '已中止 — 请勿组装', tone: 'danger' },
});
export type PackScanResult = keyof typeof PACK_SCAN_RESULTS;

/**
 * Topshirish / «Ombordan qabul» skaneri natijalari. Stocker `DISPATCH_RESULT` va
 * `HANDOVER_RESULT` ning birlashmasi: skan to'rtta savolga javob beradi — paket jismonan bormi,
 * oraliqda bekor arizasi tushmaganmi, paket o'z partiyasidanmi, ikki marta sanalmaganmi.
 */
export const DISPATCH_SCAN_RESULTS = def({
  ok: { uz: 'Qabul qilindi', ru: 'Принято', en: 'Accepted', zh: '已接收', tone: 'success' },
  already: { uz: 'Allaqachon skanerlangan', ru: 'Уже отсканировано', en: 'Already scanned', zh: '已扫描过', tone: 'warning' },
  blocked: { uz: 'To‘xtatilgan (bekor qilingan)', ru: 'Заблокировано (отменён)', en: 'Stopped (cancelled)', zh: '已中止（取消）', tone: 'danger' },
  cancel_requested: { uz: 'Bekor arizasi bor — paketni bermang', ru: 'Есть заявка на отмену — пакет не выдавать', en: 'A cancellation request is open — do not hand the parcel over', zh: '存在取消申请 — 请勿交付包裹', tone: 'danger' },
  other_batch: { uz: 'Boshqa partiyaning paketi', ru: 'Пакет из другой партии', en: 'The parcel belongs to another batch', zh: '包裹属于其他批次', tone: 'danger' },
  /**
   * HAMKOR KURYER Paketi FAQAT JO'NATMA RAQAMI BILAN (Stocker `EMU_ORDER_ID`, 2026-10-20).
   * Tashuvchining skaneri o'z jo'natma raqamini o'qiydi; buyurtma raqami yoki posilka kodi bilan
   * topshirilgan paket tashuvchi tizimida tanilmaydi. Yadroda kuryer NOMI yo'q — qoida fulfillment
   * profilidan keladi (`fulfillmentProfile().trackingOnly`).
   */
  carrier_tracking_only: { uz: 'Jo‘natma raqami kerak', ru: 'Нужен номер отправления', en: 'The shipment number is required', zh: '需要运单号', tone: 'warning' },
  not_packed: { uz: 'Hali yig‘ilmagan', ru: 'Ещё не собран', en: 'Not assembled yet', zh: '尚未组装', tone: 'danger' },
  not_dispatched: { uz: 'Ombor hali topshirmagan', ru: 'Склад ещё не выдал', en: 'The warehouse has not dispatched it yet', zh: '仓库尚未出库', tone: 'danger' },
  wrong_state: { uz: 'Noto‘g‘ri bosqich', ru: 'Неверный этап', en: 'Wrong step', zh: '步骤不正确', tone: 'danger' },
  not_found: { uz: 'Posilka topilmadi', ru: 'Посылка не найдена', en: 'Parcel not found', zh: '未找到包裹', tone: 'danger' },
});
export type DispatchScanResult = keyof typeof DISPATCH_SCAN_RESULTS;

// ------------------------------------------------------------------ Yig'ish to'siqlari
/**
 * BUYURTMA YIG'ISHGA CHIQMAYDIGAN SABABLAR (Stocker `cache/eligibility.js` `REASONS`).
 * Har to'siq — `wms.order_blocks` dagi qator; yig'ish, yakunlash, topshirish va «Ombordan
 * qabul» skanlari shu YAGONA darvozadan o'tadi.
 */
export const ORDER_BLOCK_KINDS = def({
  cancel_request: { uz: 'Bekor arizasi', ru: 'Заявка на отмену', en: 'Cancellation request', zh: '取消申请', tone: 'danger' },
  marking: { uz: 'Markirovka yo‘q', ru: 'Нет маркировки', en: 'Marking missing', zh: '缺少标识码', tone: 'danger' },
  no_stock: { uz: 'Tovar yo‘q', ru: 'Нет товара', en: 'Out of stock', zh: '无库存', tone: 'warning' },
});
export type OrderBlockKind = keyof typeof ORDER_BLOCK_KINDS;

/** To'siq skanni JISMONAN imkonsiz qiladimi (Stocker `UNSCANNABLE_REASONS`) — qo'lda ham o'tkazilmaydi. */
export const UNSCANNABLE_BLOCKS: readonly OrderBlockKind[] = ['cancel_request', 'marking'];

// ------------------------------------------------------------------ Bekor arizalari
/**
 * BEKOR ARIZASI — "bu buyurtmani bekor qiling" degan so'rov (Telegram/CRM/support).
 * Qaror daraxti Stocker `orders/cancelRequests.js` `decide()` ning neytral ko'chirmasi:
 * chegara — YIG'ILISH emas, TOPSHIRISH. O'z yetkazishimizda (`own_courier`) esa chegara
 * "yig'ildi": paket tashuvchiga emas, o'z operatsion bo'limimizga o'sha zahoti beriladi.
 */
export const CANCEL_DECISIONS = def({
  stopped: { uz: 'To‘xtatildi (paket omborda)', ru: 'Остановлен (пакет на складе)', en: 'Stopped (the parcel is in the warehouse)', zh: '已中止（包裹在仓库）', tone: 'success' },
  channel: { uz: 'Kanalga uzatildi', ru: 'Передано в канал', en: 'Passed to the channel', zh: '已转交渠道', tone: 'info' },
  handover: { uz: 'Menejer qabulda ko‘radi', ru: 'Менеджер увидит при приёмке', en: 'The manager will see it at handover', zh: '经理将在交接时看到', tone: 'warning' },
  delivery: { uz: 'Yetkazishga uzatildi', ru: 'Передано в доставку', en: 'Passed to delivery', zh: '已转交配送', tone: 'info' },
  carrier: { uz: 'Tashuvchiga uzatildi', ru: 'Передано перевозчику', en: 'Passed to the carrier', zh: '已转交承运商', tone: 'warning' },
  no_route: { uz: 'Yo‘l sozlanmagan — qo‘lda', ru: 'Маршрут не настроен — вручную', en: 'No route configured — handle manually', zh: '未配置路由 — 需人工处理', tone: 'danger' },
  not_found: { uz: 'Buyurtma topilmadi — qo‘lda', ru: 'Заказ не найден — вручную', en: 'Order not found — handle manually', zh: '未找到订单 — 需人工处理', tone: 'danger' },
});
export type CancelDecision = keyof typeof CANCEL_DECISIONS;

/** Qaysi qarorlarda ODAM aralashuvi kerak (Stocker `MANUAL`): javobda mas'ullar belgilanadi. */
export const MANUAL_CANCEL_DECISIONS: readonly CancelDecision[] = ['not_found', 'no_route'];

export const CANCEL_REQUEST_STATUSES = def({
  open: { uz: 'Ochiq', ru: 'Открыта', en: 'Open', zh: '未处理', tone: 'warning' },
  applied: { uz: 'Qo‘llandi', ru: 'Применена', en: 'Applied', zh: '已应用', tone: 'success' },
  expired: { uz: 'Muddati o‘tdi', ru: 'Истекла', en: 'Expired', zh: '已过期', tone: 'neutral' },
  dismissed: { uz: 'Bekor qilindi (xato ariza)', ru: 'Отклонена (ошибочная)', en: 'Dismissed (wrong request)', zh: '已驳回（误报）', tone: 'neutral' },
});
export type CancelRequestStatus = keyof typeof CANCEL_REQUEST_STATUSES;

/** `decideCancelRequest()` kirishi — posilka/navbat holatining PUR surati. */
export interface CancelOrderState {
  /** Posilka kuryer profili: marketplace | own_courier | carrier | pickup. */
  carrier: string | null;
  /** Yig'ilganmi (posilka yasalgan). */
  packed: boolean;
  /** Topshirish skani o'tganmi. */
  dispatched: boolean;
  /** «Ombordan qabul» (vakil paketni olgan) bo'lganmi. */
  handedOver: boolean;
  /** Shu buyurtmada ochiq ariza allaqachon bormi. */
  already?: boolean;
}

export interface CancelVerdict {
  decision: CancelDecision;
  /** Ariza takror (oldingisi hali ochiq). */
  already: boolean;
  /** Yig'ilgan paket: javob matni va UI boshqacha — "mesok javonda ajratib qo'yiladi". */
  packed: boolean;
  /** Paket ombordan chiqib bo'lgan: WMS endi to'xtata olmaydi, faqat ogohlantiradi. */
  gone: boolean;
}

/**
 * QARORNI HISOBLAYDI — hech narsa yozmaydi va hech qayerga chiqmaydi (Stocker: "ataylab PUR",
 * qaror daraxti fixture bilan, tarmoqsiz sinaladi).
 */
export function decideCancelRequest(order: CancelOrderState | null): CancelVerdict {
  if (!order) return { decision: 'not_found', already: false, packed: false, gone: false };
  const already = order.already === true;
  const packed = order.packed === true;
  // O'Z YETKAZISHIMIZ: paket yig'ilishi bilan operatsion bo'limga (delivery) o'tadi — chegara shu.
  const own = order.carrier === 'own_courier';
  const gone = order.handedOver || (own && (packed || order.dispatched));
  if (!gone) {
    // Marketplace oqimida bekorni kanalning O'ZI tasdiqlaydi — ariza hali qaror emas,
    // lekin paket baribir to'xtaydi (to'siq har qanday qarorda qo'yiladi).
    if (order.carrier === 'marketplace') return { decision: 'channel', already, packed, gone };
    return { decision: 'stopped', already, packed, gone };
  }
  if (order.carrier === 'marketplace') return { decision: 'handover', already, packed, gone };
  if (own) return { decision: 'delivery', already, packed, gone };
  if (order.carrier === 'carrier') return { decision: 'carrier', already, packed, gone };
  return { decision: 'no_route', already, packed, gone };
}

// ------------------------------------------------------------------ Bekor kuzatuvi (72 soat)
/** Bekor kuzatuvi oynasi (Stocker `cancelWatchHours`, standart 72 soat). */
export const CANCEL_WATCH_HOURS = 72;

const DAY_MS = 86_400_000;

/** YYYY-MM-DD (Toshkent) — `businessDay` bilan bir xil shakl. */
function dayKey(ms: number): string {
  return new Date(ms + 5 * 3_600_000).toISOString().slice(0, 10);
}

/**
 * KUZATUV MUDDATI: `hours` soat, DAM OLISH kunlaridan keyinga suriladi.
 *
 * Nega suriladi (Stocker `uzum/cancelSync.js`): dam olish kuni ombor buyurtmalarni
 * jo'natmaydi — ya'ni bekor bo'lganini hali ham bilishimiz kerak, aks holda ombor
 * bekorligini bilmay jo'natadi va ortiqcha logistika xarajati chiqadi.
 * Chegara 7 kun — mangu tsikldan yomoni yo'q.
 */
export function cancelWatchDeadline(
  arrivedMs: number,
  opts: { hours?: number; restWeekdays?: readonly number[]; restDates?: readonly string[] } = {},
): number {
  const hours = Number(opts.hours) > 0 ? Number(opts.hours) : CANCEL_WATCH_HOURS;
  const restWeekdays = new Set(opts.restWeekdays ?? []);
  const restDates = new Set(opts.restDates ?? []);
  const isRest = (ms: number) => restDates.has(dayKey(ms)) || restWeekdays.has(new Date(ms + 5 * 3_600_000).getUTCDay());
  let deadline = arrivedMs + hours * 3_600_000;
  for (let i = 0; i < 7 && isRest(deadline); i++) deadline += DAY_MS;
  return deadline;
}

/**
 * Qaytarishlar. IKKI BOSQICH (Stocker `packing/returns.js`): menejer paketni skanerlab qaytaradi
 * (`in_transit` — oraliqda paket "yo'lda") → ombor xodimi uni javonga oladi (`received`).
 * Ikkinchi tekshiruv ataylab boshqa odamda: bitta kalit bo'lsa menejer o'zi qaytarib, o'zi
 * qabul qilib qo'ya olardi.
 */
export const RETURN_STATUSES = def({
  expected: { uz: 'Kutilmoqda', ru: 'Ожидается', en: 'Pending', zh: '待处理', tone: 'warning' },
  in_transit: { uz: 'Yo‘lda', ru: 'В пути', en: 'In transit', zh: '在途', tone: 'violet' },
  received: { uz: 'Qabul qilindi', ru: 'Принят', en: 'Accepted', zh: '已接收', tone: 'info' },
  restocked: { uz: 'Omborga qaytarildi', ru: 'Оприходован', en: 'Returned to the warehouse', zh: '已退回仓库', tone: 'success' },
  written_off: { uz: 'Hisobdan chiqarildi', ru: 'Списан', en: 'Written off', zh: '已报损', tone: 'danger' },
});
export type ReturnStatus = keyof typeof RETURN_STATUSES;

/** Qaytarish bosqichlari: out — menejer qaytardi, in — ombor qabul qildi. */
export const RETURN_STAGES = def({
  out: { uz: 'Omborga qaytarish', ru: 'Возврат на склад', en: 'Return to the warehouse', zh: '退回仓库', tone: 'warning' },
  in: { uz: 'Qaytarishni qabul qilish', ru: 'Приём возврата', en: 'Accept the return', zh: '接收退货', tone: 'info' },
});
export type ReturnStage = keyof typeof RETURN_STAGES;

/** Qaytarish skaneri natijalari (Stocker `RETURN_RESULT` ning aynan o'zi). */
export const RETURN_SCAN_RESULTS = def({
  ok: { uz: 'Qabul qilindi', ru: 'Принято', en: 'Accepted', zh: '已接收', tone: 'success' },
  duplicate: { uz: 'Allaqachon shu bosqichdan o‘tgan', ru: 'Уже прошёл этот этап', en: 'Already past this step', zh: '已完成该步骤', tone: 'warning' },
  not_canceled: { uz: 'Bekor emas — qaytarilmaydi', ru: 'Не отменён — возврат не нужен', en: 'Not cancelled — not returnable', zh: '未取消，不可退回', tone: 'danger' },
  not_sent: { uz: 'Paket ombordan chiqmagan', ru: 'Пакет со склада не выходил', en: 'The parcel never left the warehouse', zh: '包裹未出库', tone: 'danger' },
  not_returned: { uz: 'Menejer hali qaytarmagan', ru: 'Менеджер ещё не вернул', en: 'The manager has not returned it yet', zh: '经理尚未退回', tone: 'danger' },
  not_found: { uz: 'Topilmadi', ru: 'Не найдено', en: 'Not found', zh: '未找到', tone: 'danger' },
  no_invoice: { uz: 'Qaytarish akti tanlanmagan', ru: 'Акт возврата не выбран', en: 'No return document selected', zh: '未选择退货单', tone: 'danger' },
  invoice_locked: { uz: 'Ombor qabulni boshlagan — qo‘shib bo‘lmaydi', ru: 'Склад начал приём — добавить нельзя', en: 'The warehouse started accepting — cannot add', zh: '仓库已开始接收，无法添加', tone: 'danger' },
  other_invoice: { uz: 'Paket boshqa aktda', ru: 'Пакет в другом акте', en: 'The parcel belongs to another document', zh: '包裹属于其他单据', tone: 'danger' },
});
export type ReturnScanResult = keyof typeof RETURN_SCAN_RESULTS;

/**
 * Qaytarish akti holati — SAQLANMAYDI, hisoblanadi (Stocker `RETURN_INVOICE_STATE`):
 * ombor birinchi paketni qabul qilgach akt QULFLANADI (omborchi sanayotgan ro'yxat o'zgarmasin).
 */
export function returnInvoiceState(counts: { total: number; accepted: number }): 'open' | 'accepting' | 'closed' {
  if (counts.accepted > 0 && counts.accepted >= counts.total) return 'closed';
  if (counts.accepted > 0) return 'accepting';
  return 'open';
}

export const RETURN_SOURCES = def({
  cancel: { uz: 'Bekor qilingan buyurtma', ru: 'Отменённый заказ', en: 'Cancelled order', zh: '已取消订单', tone: 'neutral' },
  delivery: { uz: 'Kuryerdan qaytdi', ru: 'Возврат от курьера', en: 'Returned by the courier', zh: '配送员退回', tone: 'info' },
  manual: { uz: 'Qo‘lda', ru: 'Вручную', en: 'Manual', zh: '手动', tone: 'neutral' },
});
export type ReturnSource = keyof typeof RETURN_SOURCES;

export const RETURN_CONDITIONS = def({
  good: { uz: 'Yaroqli', ru: 'Годный', en: 'Good', zh: '良品', tone: 'success' },
  damaged: { uz: 'Shikastlangan', ru: 'Повреждён', en: 'Damaged', zh: '破损', tone: 'danger' },
});
export type ReturnCondition = keyof typeof RETURN_CONDITIONS;

// ------------------------------------------------------------------ Nakladnoylar (invoice)
/**
 * NAKLADNOY — ombor hujjati: qaysi paketlar bir yo'la chiqadi (yoki qaytadi) va qaysilari
 * topshirilmay qoldi. Stocker'da uch varaq: FBS · Qaytarish · FBO.
 *   fbs    — marketplace FBS nakladnoyi (kanal adapteri import qiladi, `integrations`);
 *   fbo    — FBO yetkazmasi (har dona o'z ID si bilan);
 *   return — qaytarish akti (menejer qaytargan paketlar yig'iladi).
 */
export const INVOICE_KINDS = def({
  fbs: { uz: 'FBS nakladnoyi', ru: 'Накладная FBS', en: 'FBS invoice', zh: 'FBS 运单', tone: 'info' },
  fbo: { uz: 'FBO nakladnoyi', ru: 'Накладная FBO', en: 'FBO invoice', zh: 'FBO 运单', tone: 'violet' },
  return: { uz: 'Qaytarish akti', ru: 'Акт возврата', en: 'Return document', zh: '退货单', tone: 'warning' },
});
export type InvoiceKind = keyof typeof INVOICE_KINDS;

/**
 * Nakladnoy holati. `open` — qo'shish mumkin; `accepting` — ish boshlangan, tarkib QULFLANGAN
 * (omborchi sanayotgan ro'yxat o'zgarmasin); `closed` — hammasi o'tdi; `canceled` — kanal bekor qildi.
 */
export const INVOICE_STATUSES = def({
  open: { uz: 'Ochiq', ru: 'Открыта', en: 'Open', zh: '未关闭', tone: 'info' },
  accepting: { uz: 'Ish boshlandi', ru: 'В работе', en: 'In progress', zh: '处理中', tone: 'accent' },
  closed: { uz: 'Yopildi', ru: 'Закрыта', en: 'Closed', zh: '已关闭', tone: 'success' },
  canceled: { uz: 'Bekor qilindi', ru: 'Отменена', en: 'Cancelled', zh: '已取消', tone: 'danger' },
});
export type InvoiceStatus = keyof typeof INVOICE_STATUSES;

/** Nakladnoydagi buyurtma qatori. */
export const INVOICE_ORDER_STATUSES = def({
  pending: { uz: 'Kutilmoqda', ru: 'Ожидается', en: 'Pending', zh: '待处理', tone: 'warning' },
  done: { uz: 'Topshirildi', ru: 'Передан', en: 'Handed over', zh: '已交付', tone: 'success' },
  missing: { uz: 'Topshirilmadi', ru: 'Не передан', en: 'Not handed over', zh: '未交付', tone: 'danger' },
});
export type InvoiceOrderStatus = keyof typeof INVOICE_ORDER_STATUSES;

/** Chop etish vazifalari va stansiyalar. */
export const PRINT_JOB_STATUSES = def({
  queued: { uz: 'Navbatda', ru: 'В очереди', en: 'Queued', zh: '排队中', tone: 'info' },
  sent: { uz: 'Yuborildi', ru: 'Отправлено', en: 'Sent', zh: '已发送', tone: 'accent' },
  printed: { uz: 'Chop etildi', ru: 'Напечатано', en: 'Printed', zh: '已打印', tone: 'success' },
  failed: { uz: 'Xato', ru: 'Ошибка', en: 'Error', zh: '错误', tone: 'danger' },
});
export type PrintJobStatus = keyof typeof PRINT_JOB_STATUSES;

export const STATION_STATUSES = def({
  pending: { uz: 'Ulanishni kutmoqda', ru: 'Ожидает подключения', en: 'Waiting for a connection', zh: '等待连接', tone: 'warning' },
  active: { uz: 'Faol', ru: 'Активна', en: 'Active', zh: '启用', tone: 'success' },
  disabled: { uz: 'O‘chirilgan', ru: 'Отключена', en: 'Disabled', zh: '已关闭', tone: 'neutral' },
});
export type StationStatus = keyof typeof STATION_STATUSES;

/** Harakat manbasi (ledger `refType`). */
export const MOVEMENT_REF_TYPES = def({
  supply: { uz: 'Yetkazma', ru: 'Поставка', en: 'Supply', zh: '到货单', tone: 'success' },
  order: { uz: 'Buyurtma', ru: 'Заказ', en: 'Order', zh: '订单', tone: 'violet' },
  parcel: { uz: 'Posilka', ru: 'Посылка', en: 'Parcel', zh: '包裹', tone: 'info' },
  inventory: { uz: 'Inventarizatsiya', ru: 'Инвентаризация', en: 'Stocktake', zh: '盘点', tone: 'warning' },
  return: { uz: 'Qaytarish', ru: 'Возврат', en: 'Return', zh: '退货', tone: 'info' },
  manual: { uz: 'Qo‘lda', ru: 'Вручную', en: 'Manual', zh: '手动', tone: 'neutral' },
});
export type MovementRefType = keyof typeof MOVEMENT_REF_TYPES;

/**
 * Yorliq shablonlari (server reyestri `wms/labels` bilan bir xil kodlar).
 * Yangi shablon = server tomonda yangi fayl + `register()`; bu ro'yxat faqat UI nomlari uchun.
 */
export const LABEL_TEMPLATES = {
  sku_40x30: { uz: 'Tovar yorlig‘i 40×30', ru: 'Этикетка товара 40×30', en: 'Product label 40×30', zh: '商品标签 40×30', widthMm: 40, heightMm: 30, target: 'sku' },
  parcel_100x100: { uz: 'Posilka yorlig‘i 100×100', ru: 'Этикетка посылки 100×100', en: 'Parcel label 100×100', zh: '包裹标签 100×100', widthMm: 100, heightMm: 100, target: 'parcel' },
  fragile_mark: { uz: '“Ehtiyot bo‘ling” belgisi', ru: 'Знак «Хрупкое»', en: '“Fragile” sticker', zh: '“易碎”标识', widthMm: 100, heightMm: 100, target: 'parcel' },
  external_pdf: { uz: 'Marketplace yorlig‘i (tashqi PDF)', ru: 'Этикетка маркетплейса', en: 'Marketplace label (external PDF)', zh: '平台面单（外部 PDF）', widthMm: 100, heightMm: 100, target: 'parcel' },
  cell_60x40: { uz: 'Yacheyka yorlig‘i 60×40', ru: 'Этикетка ячейки 60×40', en: 'Bin label 60×40', zh: '库位标签 60×40', widthMm: 60, heightMm: 40, target: 'cell' },
} as const satisfies Record<string, L10n & { widthMm: number; heightMm: number; target: 'sku' | 'parcel' | 'cell' }>;
export type LabelTemplateCode = keyof typeof LABEL_TEMPLATES;

/** Xarajat materiallari o'lchov birliklari. */
export const MATERIAL_UNITS = {
  dona: { uz: 'dona', ru: 'шт', en: 'pcs', zh: '件' },
  rulon: { uz: 'rulon', ru: 'рулон', en: 'roll', zh: '卷' },
  metr: { uz: 'metr', ru: 'м', en: 'm', zh: '米' },
  kg: { uz: 'kg', ru: 'кг', en: 'kg', zh: '千克' },
  quti: { uz: 'quti', ru: 'коробка', en: 'box', zh: '箱' },
} as const satisfies Record<string, L10n>;
export type MaterialUnit = keyof typeof MATERIAL_UNITS;

/** "Kam qoldiq" chegarasi (mavjud ≤ shu son) — hamkor va ombor ekranlarida ajratib ko'rsatiladi. */
export const LOW_STOCK_THRESHOLD = 5;

/** Yetkazmaning "yo'lda" (kiruvchi) hisoblanadigan holatlari. */
export const INBOUND_SUPPLY_STATUSES = ['submitted', 'arrived', 'receiving'] as const;

/** Statuslar guruhi bo'yicha ta'rif (UI `WmsPill` uchun). */
export const WMS_STATUS_GROUPS = {
  zoneKind: ZONE_KINDS,
  cellKind: CELL_KINDS,
  reservation: RESERVATION_STATUSES,
  orderQueue: ORDER_QUEUE_STATUSES,
  packSession: PACK_SESSION_STATUSES,
  packScan: PACK_SCAN_RESULTS,
  dispatchScan: DISPATCH_SCAN_RESULTS,
  return: RETURN_STATUSES,
  returnSource: RETURN_SOURCES,
  returnCondition: RETURN_CONDITIONS,
  returnStage: RETURN_STAGES,
  returnScan: RETURN_SCAN_RESULTS,
  orderBlock: ORDER_BLOCK_KINDS,
  cancelDecision: CANCEL_DECISIONS,
  cancelRequest: CANCEL_REQUEST_STATUSES,
  invoiceKind: INVOICE_KINDS,
  invoice: INVOICE_STATUSES,
  invoiceOrder: INVOICE_ORDER_STATUSES,
  printJob: PRINT_JOB_STATUSES,
  station: STATION_STATUSES,
  movementRef: MOVEMENT_REF_TYPES,
} as const;
export type WmsStatusGroup = keyof typeof WMS_STATUS_GROUPS;

export function wmsDef(group: WmsStatusGroup, code: string | null | undefined): WmsDef {
  const map = WMS_STATUS_GROUPS[group] as Record<string, WmsDef>;
  return (code && map[code]) || { uz: code ?? '—', ru: code ?? '—', tone: 'neutral' };
}

export function wmsOptions(group: WmsStatusGroup) {
  return Object.entries(WMS_STATUS_GROUPS[group] as Record<string, WmsDef>).map(([value, d]) => ({ value, ...d }));
}

/** m³ ni ko'rinishga: 0.0123 → "0,012 m³". */
export function formatM3(v: number | null | undefined, digits = 3): string {
  const n = typeof v === 'number' && Number.isFinite(v) ? v : 0;
  return `${n.toFixed(digits).replace('.', ',')} m³`;
}

/** Hujjat raqamlari (ketma-ketlikdan): SUP-000123, PT-000045, PCL-0001234, RET-000012, INV-000003, NKL-000007. */
export const DOC_PREFIX = {
  supply: { prefix: 'SUP', digits: 6 },
  batch: { prefix: 'PT', digits: 6 },
  parcel: { prefix: 'PCL', digits: 7 },
  return: { prefix: 'RET', digits: 6 },
  inventory: { prefix: 'INV', digits: 6 },
  invoice: { prefix: 'NKL', digits: 6 },
} as const;
export type DocKind = keyof typeof DOC_PREFIX;

export function docNumber(kind: DocKind, n: number): string {
  const d = DOC_PREFIX[kind];
  return `${d.prefix}-${String(n).padStart(d.digits, '0')}`;
}
